// @ts-nocheck
import type { ItemDefinition } from '../../../../../shims/appStub';
import type { ValidationIssue } from '../../diagnostics';
import type { ItemVariableRef } from '../../types/binding';
import type { BindingResolutionResult, BindingWriteResult } from '../binding/types';
import {
  parseItemVariables,
  readVariable,
  deleteVariable,
  writeItemVariablesToColumn,
  writeVariable,
  type ItemVariableType,
} from '../variables/itemVariableStorage';

export function resolveItemVariableFromItem(
  item: ItemDefinition,
  key: string,
): BindingResolutionResult {
  if (!key?.trim()) {
    const issue: ValidationIssue = {
      severity: 'warning',
      code: 'semantic.invalid_data_ref',
      message: 'Item variable key must be non-empty.',
    };
    return { found: false, source: 'itemVariable', diagnostics: [issue] };
  }

  const bag = parseItemVariables(item.itemVariablesJson ?? '').bag;
  const read = readVariable(bag, key);
  return {
    found: read.found,
    value: read.value,
    source: 'itemVariable',
  };
}

export function inferItemVariableWriteType(
  item: ItemDefinition,
  key: string,
  explicitType?: ItemVariableType,
): ItemVariableType {
  if (explicitType) {
    return explicitType;
  }
  const bag = parseItemVariables(item.itemVariablesJson ?? '').bag;
  const existing = bag[key];
  const t = existing?.t;
  if (
    t === 'string'
    || t === 'integer'
    || t === 'float'
    || t === 'boolean'
    || t === 'color'
    || t === 'datetime'
    || t === 'itemRef'
    || t === 'list'
  ) {
    return t;
  }
  // Legacy table/fileTable bag tags are not writable; default for new writes.
  return 'string';
}

export function applyItemVariableWrite(
  item: ItemDefinition,
  key: string,
  type: ItemVariableType,
  value: unknown,
): BindingWriteResult & { updatedItem?: ItemDefinition } {
  const bag = parseItemVariables(item.itemVariablesJson ?? '').bag;
  const writeResult = writeVariable(bag, key, type, value);
  if (!writeResult.ok) {
    const issue: ValidationIssue = {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: writeResult.message,
    };
    return { success: false, diagnostics: [issue] };
  }

  if (!writeResult.changed) {
    return {
      success: true,
      updatedItemVariablesJson: item.itemVariablesJson ?? '',
      updatedItem: item,
    };
  }

  return {
    success: true,
    updatedItemVariablesJson: writeItemVariablesToColumn(writeResult.bag),
    updatedItem: {
      ...item,
      itemVariablesJson: writeItemVariablesToColumn(writeResult.bag),
      lastEdited: Date.now(),
    },
  };
}

export function applyItemVariableDelete(
  item: ItemDefinition,
  key: string,
): BindingWriteResult & { updatedItem?: ItemDefinition } {
  const bag = parseItemVariables(item.itemVariablesJson ?? '').bag;
  const deleteResult = deleteVariable(bag, key);
  if (!deleteResult.ok) {
    const issue: ValidationIssue = {
      severity: 'error',
      code: 'semantic.invalid_data_ref',
      message: deleteResult.message,
    };
    return { success: false, diagnostics: [issue] };
  }

  if (!deleteResult.changed) {
    return {
      success: true,
      updatedItemVariablesJson: item.itemVariablesJson ?? '',
      updatedItem: item,
    };
  }

  return {
    success: true,
    updatedItemVariablesJson: writeItemVariablesToColumn(deleteResult.bag),
    updatedItem: {
      ...item,
      itemVariablesJson: writeItemVariablesToColumn(deleteResult.bag),
      lastEdited: Date.now(),
    },
  };
}

export function readItemVariableRef(
  ref: ItemVariableRef,
  item: ItemDefinition,
): BindingResolutionResult {
  return resolveItemVariableFromItem(item, ref.key);
}
